import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { adminApi } from '../services/api';
import { Player, PlayerRole, BanInfo } from '../types';
import Pager from '../components/Pager';

const DURATION_OPTIONS = [
  { label: 'Indefinite', value: undefined },
  { label: '1 hour', value: 1 },
  { label: '6 hours', value: 6 },
  { label: '12 hours', value: 12 },
  { label: '24 hours', value: 24 },
  { label: '3 days', value: 72 },
  { label: '7 days', value: 168 },
  { label: '30 days', value: 720 },
];

function isActiveBan(ban?: BanInfo): boolean {
  if (!ban?.isBanned) return false;
  if (!ban.bannedUntil) return true;
  return new Date(ban.bannedUntil) > new Date();
}

function isBanExpired(ban?: BanInfo): boolean {
  return ban?.isBanned === true && !!ban.bannedUntil && new Date(ban.bannedUntil) <= new Date();
}

function roleBadge(role: PlayerRole) {
  const styles: Record<PlayerRole, React.CSSProperties> = {
    [PlayerRole.Admin]: { background: '#7f1d1d', color: '#fca5a5', border: '1px solid #ef4444' },
    [PlayerRole.Moderator]: { background: '#1e3a5f', color: '#93c5fd', border: '1px solid #3b82f6' },
    [PlayerRole.User]: { background: '#1a1a2e', color: '#9ca3af', border: '1px solid #374151' },
  };
  return (
    <span style={{
      ...styles[role],
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      fontFamily: 'monospace',
      textTransform: 'uppercase',
    }}>
      {role}
    </span>
  );
}

interface BanModal {
  userId: string;
  username: string;
}

interface ActivityRow {
  id: string;
  action: string;
  ip?: string;
  timestamp: string;
}

interface EarningRow {
  id: string;
  credits: number;
  experience: number;
  reputation: number;
  source: string;
  timestamp: string;
}

interface EarningsData {
  totals: { credits: number; experience: number; reputation: number };
  entries: EarningRow[];
}

interface InfectionStats {
  total: number;
  active: number;
  installing: number;
  onNpcNodes: number;
  onHumanNodes: number;
  infectedNodes: number;
  distinctAttackers: number;
  attackerTracked: number;
  connectionLost: number;
}

type AdminTab = 'users' | 'infections' | 'software';

interface SoftwareStatRow {
  type: string;
  maxVersion: number;
  username: string;
}

// Server-sortable columns (doc-backed). Status (derived) and Last action (in the
// ActivityLog container) can't be sorted server-side, so they aren't sort keys.
type SortKey = 'username' | 'role' | 'lastLogin';
type SortDir = 'asc' | 'desc';

// Epoch ms for an ISO date, or 0 when missing/invalid (sorts as "oldest").
function timeOf(iso?: string): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return isNaN(t) ? 0 : t;
}

// Days after which an inactive account is auto-deleted (must match the backend).
const INACTIVE_DELETE_DAYS = 60;
// Show the "toBeDeleted" warning once this many days (or fewer) remain before removal.
const DELETE_WARN_DAYS = 3;
const PAGE_SIZE = 20;

function fmtDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

// Coloured pill for a login-source portal (itch / crazygames / direct).
function sourceBadge(source?: string) {
  if (!source) return <span style={{ color: '#555' }}>—</span>;
  const colors: Record<string, string> = { itch: '#f472b6', crazygames: '#fb923c', direct: '#9ca3af' };
  return <span style={{ color: colors[source] ?? '#9ca3af', fontSize: '0.78rem' }}>{source}</span>;
}

function statCard(label: string, value: number, color: string, big = false) {
  return (
    <div key={label} className="card" style={{ padding: '0.85rem 1rem' }}>
      <div style={{ color: '#888', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>
        {label}
      </div>
      <div style={{ color, fontFamily: 'monospace', fontWeight: 700, fontSize: big ? '1.8rem' : '1.4rem' }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

export default function Admin() {
  const { isAuthenticated, role, userId: myId, authChecked } = useAuthStore();
  const navigate = useNavigate();

  const [users, setUsers] = useState<Player[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Ban modal state
  const [banModal, setBanModal] = useState<BanModal | null>(null);
  const [banReason, setBanReason] = useState('');
  const [banDuration, setBanDuration] = useState<number | undefined>(undefined);
  const [actionError, setActionError] = useState('');

  // Activity: last-action time per user (list column) + the per-user log modal.
  const [lastActivity, setLastActivity] = useState<Record<string, string>>({});
  const [activityModal, setActivityModal] = useState<{ userId: string; username: string } | null>(null);
  const [activityEntries, setActivityEntries] = useState<ActivityRow[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Earnings ledger modal.
  const [earningsModal, setEarningsModal] = useState<{ userId: string; username: string } | null>(null);
  const [earningsData, setEarningsData] = useState<EarningsData | null>(null);
  const [earningsLoading, setEarningsLoading] = useState(false);

  // Server-side sort of the users table. Default: most-recently-logged-in first.
  const [sortKey, setSortKey] = useState<SortKey>('lastLogin');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Tabs + system-wide infection monitor.
  const [tab, setTab] = useState<AdminTab>('users');
  const [infStats, setInfStats] = useState<InfectionStats | null>(null);
  const [infLoading, setInfLoading] = useState(false);
  const [infError, setInfError] = useState('');
  const [infRefresh, setInfRefresh] = useState(0);

  // System-wide highest-software-level monitor.
  const [swStats, setSwStats] = useState<SoftwareStatRow[] | null>(null);
  const [swLoading, setSwLoading] = useState(false);
  const [swError, setSwError] = useState('');
  const [swRefresh, setSwRefresh] = useState(0);

  const isAdmin = role === PlayerRole.Admin;
  const isMod = role === PlayerRole.Moderator;

  // Route guard: only Admin/Moderator may stay on this page.
  useEffect(() => {
    if (!authChecked) return;   // wait for the initial auth check before deciding
    if (!isAuthenticated || (!isAdmin && !isMod)) navigate('/');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, isAuthenticated, role]);

  // Debounce the search box, and reset to the first page whenever the query changes.
  useEffect(() => {
    const id = setTimeout(() => { setPage(1); setDebouncedSearch(search.trim()); }, 400);
    return () => clearTimeout(id);
  }, [search]);

  // Load system-wide infection stats when the Infections tab is shown (and on refresh).
  useEffect(() => {
    if (tab !== 'infections') return;
    let cancelled = false;
    setInfLoading(true);
    setInfError('');
    adminApi.getInfectionStats()
      .then(res => { if (!cancelled) setInfStats(res.data as InfectionStats); })
      .catch(() => { if (!cancelled) setInfError('Failed to load infection stats.'); })
      .finally(() => { if (!cancelled) setInfLoading(false); });
    return () => { cancelled = true; };
  }, [tab, infRefresh]);

  // Load highest-software-level stats when the Software tab is shown (and on refresh).
  useEffect(() => {
    if (tab !== 'software') return;
    let cancelled = false;
    setSwLoading(true);
    setSwError('');
    adminApi.getSoftwareStats()
      .then(res => { if (!cancelled) setSwStats((res.data as SoftwareStatRow[]) ?? []); })
      .catch(() => { if (!cancelled) setSwError('Failed to load software stats.'); })
      .finally(() => { if (!cancelled) setSwLoading(false); });
    return () => { cancelled = true; };
  }, [tab, swRefresh]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.getUsers({ page, size: PAGE_SIZE, search: debouncedSearch, sort: sortKey, dir: sortDir });
      const data = res.data as { items: Player[]; total: number };
      setUsers(data.items ?? []);
      setTotal(data.total ?? 0);
      // Last-action time per user for the column + status (non-fatal if it fails).
      try {
        const act = await adminApi.getLastActivity();
        const map: Record<string, string> = {};
        (act.data as { playerId: string; lastActionAt: string }[] | null)?.forEach(r => { map[r.playerId] = r.lastActionAt; });
        setLastActivity(map);
      } catch { /* ignore */ }
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, sortKey, sortDir]);

  // Fetch the current page whenever it (or the query/sort) changes, once authorised.
  useEffect(() => {
    if (!authChecked || !isAuthenticated || (!isAdmin && !isMod)) return;
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUsers, authChecked, isAuthenticated, role]);

  const openEarnings = async (user: Player) => {
    setEarningsModal({ userId: user.id, username: user.username });
    setEarningsData(null);
    setEarningsLoading(true);
    try {
      const res = await adminApi.getUserEarnings(user.id);
      setEarningsData(res.data as EarningsData);
    } catch {
      setActionError('Failed to load earnings.');
    } finally {
      setEarningsLoading(false);
    }
  };

  const openActivity = async (user: Player) => {
    setActivityModal({ userId: user.id, username: user.username });
    setActivityEntries([]);
    setActivityLoading(true);
    try {
      const res = await adminApi.getUserActivity(user.id);
      setActivityEntries((res.data as ActivityRow[]) ?? []);
    } catch {
      setActionError('Failed to load activity.');
    } finally {
      setActivityLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setActionError('');
    try {
      const res = await adminApi.updateRole(userId, newRole);
      setUsers(prev => prev.map(u => u.id === userId ? res.data : u));
    } catch {
      setActionError('Failed to update role.');
    }
  };

  const handleWhitelistToggle = async (user: Player) => {
    setActionError('');
    try {
      const res = await adminApi.setWhitelist(user.id, !user.multiAccountWhitelisted);
      setUsers(prev => prev.map(u => u.id === user.id ? res.data : u));
    } catch {
      setActionError('Failed to update whitelist.');
    }
  };

  const openBanModal = (user: Player) => {
    setBanModal({ userId: user.id, username: user.username });
    setBanReason('');
    setBanDuration(undefined);
    setActionError('');
  };

  const handleBan = async () => {
    if (!banModal) return;
    if (!banReason.trim()) { setActionError('Reason is required.'); return; }
    setActionError('');
    try {
      const res = await adminApi.banUser(banModal.userId, banReason.trim(), banDuration);
      setUsers(prev => prev.map(u => u.id === banModal.userId ? res.data : u));
      setBanModal(null);
    } catch {
      setActionError('Failed to ban user.');
    }
  };

  const handleUnban = async (userId: string) => {
    setActionError('');
    try {
      const res = await adminApi.unbanUser(userId);
      setUsers(prev => prev.map(u => u.id === userId ? res.data : u));
    } catch {
      setActionError('Failed to unban user.');
    }
  };

  const canActOn = (target: Player) => {
    if (target.id === myId) return false;
    if (target.role === PlayerRole.Admin) return false;
    if (isMod && target.role !== PlayerRole.User) return false;
    return true;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Click a column header to sort by it (server-side); click again to flip direction.
  // Changing the sort resets to the first page.
  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'username' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const sortTh = (label: string, key: SortKey) => (
    <th
      onClick={() => toggleSort(key)}
      title="Click to sort"
      style={{
        padding: '0.75rem 1rem',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        color: sortKey === key ? 'var(--primary-color)' : undefined,
      }}
    >
      {label}{sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
    </th>
  );

  const inputStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    background: '#0f0f1a',
    border: '1px solid #333',
    borderRadius: '4px',
    color: '#e0e0e0',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  };

  const btnStyle = (variant: 'danger' | 'success' | 'neutral'): React.CSSProperties => {
    const colors = {
      danger: { bg: '#450a0a', color: '#fca5a5', border: '#ef4444' },
      success: { bg: '#052e16', color: '#86efac', border: '#22c55e' },
      neutral: { bg: '#1a1a2e', color: '#9ca3af', border: '#374151' },
    };
    const c = colors[variant];
    return {
      padding: '3px 10px',
      background: c.bg,
      color: c.color,
      border: `1px solid ${c.border}`,
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      fontFamily: 'monospace',
    };
  };

  return (
    <div>
      <h1 style={{ color: 'var(--primary-color)', marginBottom: '0.5rem' }}>
        {isAdmin ? 'Admin Panel' : 'Moderation Panel'}
      </h1>
      <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        {isAdmin
          ? 'Manage roles and bans for all users.'
          : 'Manage bans for User-role players.'}
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid #222' }}>
        {(['users', 'infections', 'software'] as AdminTab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t ? '2px solid var(--primary-color)' : '2px solid transparent',
              color: tab === t ? 'var(--primary-color)' : '#888',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
            }}
          >
            {t === 'users' ? 'Users' : t === 'infections' ? 'Infections' : 'Software'}
          </button>
        ))}
      </div>

      {error && <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</p>}
      {actionError && <p style={{ color: '#f97316', marginBottom: '1rem' }}>{actionError}</p>}

      {tab === 'users' && (<>
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...inputStyle, width: '100%', maxWidth: '360px' }}
          placeholder="Search by username…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading && users.length === 0 ? (
        <p style={{ color: '#666' }}>Loading users…</p>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #222', color: '#666', textAlign: 'left' }}>
                {sortTh('Username', 'username')}
                {sortTh('Role', 'role')}
                <th style={{ padding: '0.75rem 1rem' }}>Status</th>
                {sortTh('Last login', 'lastLogin')}
                <th style={{ padding: '0.75rem 1rem' }}>Last action</th>
                <th style={{ padding: '0.75rem 1rem' }}>First source</th>
                <th style={{ padding: '0.75rem 1rem' }}>First login</th>
                <th style={{ padding: '0.75rem 1rem' }}>Last source</th>
                <th style={{ padding: '0.75rem 1rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: '1.5rem', color: '#555', textAlign: 'center' }}>
                    No users found.
                  </td>
                </tr>
              )}
              {users.map(user => (
                <tr
                  key={user.id}
                  style={{ borderBottom: '1px solid #1a1a1a' }}
                >
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span>{user.username}</span>
                      {user.isGuest && (
                        <span
                          title="Ephemeral CrazyGames guest account (not linked to a CrazyGames user)"
                          style={{ fontSize: '0.6rem', color: '#38bdf8', border: '1px solid #38bdf8', borderRadius: '3px', padding: '0 4px' }}
                        >GUEST</span>
                      )}
                      {user.multiAccountWhitelisted && (
                        <span
                          title="Exempt from the shared-IP multi-account auto-ban"
                          style={{ fontSize: '0.6rem', color: '#4ade80', border: '1px solid #4ade80', borderRadius: '3px', padding: '0 4px' }}
                        >WL</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {roleBadge(user.role)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {isActiveBan(user.ban) ? (
                      <div>
                        <span style={{ color: '#ef4444', fontWeight: 'bold' }}>BANNED</span>
                        {user.ban?.bannedUntil && (
                          <div style={{ color: '#666', fontSize: '0.75rem' }}>
                            until {new Date(user.ban.bannedUntil).toLocaleString()}
                          </div>
                        )}
                        {!user.ban?.bannedUntil && (
                          <div style={{ color: '#666', fontSize: '0.75rem' }}>indefinite</div>
                        )}
                        {user.ban?.reason && (
                          <div style={{ color: '#888', fontSize: '0.75rem', fontStyle: 'italic' }}>
                            "{user.ban.reason}"
                          </div>
                        )}
                      </div>
                    ) : isBanExpired(user.ban) ? (
                      <span style={{ color: '#ca8a04' }}>Expired ban</span>
                    ) : (() => {
                      // Real activity = most recent of last login and last logged action
                      // (neither is bumped by passive polling).
                      const lastSeenMs = Math.max(timeOf(user.lastLoginAt), timeOf(lastActivity[user.id]));
                      const days = lastSeenMs ? Math.floor((Date.now() - lastSeenMs) / 86_400_000) : 0;
                      // Only User-role accounts are actually auto-deleted (staff exempt),
                      // so only they get the imminent-removal warning.
                      const toRemoval = INACTIVE_DELETE_DAYS - days;
                      if (user.role === PlayerRole.User && toRemoval <= DELETE_WARN_DAYS) {
                        const n = Math.max(0, toRemoval);
                        return (
                          <span
                            style={{ color: '#ef4444', fontWeight: 'bold' }}
                            title={`Inactive for ${days} days. Will be permanently removed from the database in ${n} day${n === 1 ? '' : 's'}.`}
                          >
                            toBeDeleted ({n} day{n === 1 ? '' : 's'} to removal)
                          </span>
                        );
                      }
                      return days >= 1 ? (
                        <span
                          style={{ color: '#f59e0b' }}
                          title={`No activity for ${days} day${days === 1 ? '' : 's'}. Auto-deleted after ${INACTIVE_DELETE_DAYS} days of inactivity.`}
                        >
                          Inactive ({days} day{days === 1 ? '' : 's'})
                        </span>
                      ) : (
                        <span style={{ color: '#22c55e' }}>Active</span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {fmtDateTime(user.lastLoginAt)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {fmtDateTime(lastActivity[user.id])}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>{sourceBadge(user.firstLoginSource)}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {fmtDateTime(user.firstLoginAt)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>{sourceBadge(user.lastLoginSource)}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button style={btnStyle('neutral')} onClick={() => openActivity(user)}>
                        Activity
                      </button>
                      <button style={btnStyle('neutral')} onClick={() => openEarnings(user)}>
                        Earnings
                      </button>
                    {canActOn(user) ? (
                      <>
                        {isActiveBan(user.ban) ? (
                          <button style={btnStyle('success')} onClick={() => handleUnban(user.id)}>
                            Unban
                          </button>
                        ) : (
                          <button style={btnStyle('danger')} onClick={() => openBanModal(user)}>
                            Ban
                          </button>
                        )}
                        {isBanExpired(user.ban) && (
                          <button style={btnStyle('neutral')} onClick={() => handleUnban(user.id)}>
                            Clear ban
                          </button>
                        )}
                        {isAdmin && (
                          <select
                            value={user.role}
                            onChange={e => handleRoleChange(user.id, e.target.value)}
                            style={{ ...inputStyle, padding: '3px 6px', cursor: 'pointer' }}
                          >
                            <option value={PlayerRole.User}>User</option>
                            <option value={PlayerRole.Moderator}>Moderator</option>
                          </select>
                        )}
                        <button
                          style={btnStyle(user.multiAccountWhitelisted ? 'neutral' : 'success')}
                          title="Exempt this account from the shared-IP multi-account auto-ban"
                          onClick={() => handleWhitelistToggle(user)}
                        >
                          {user.multiAccountWhitelisted ? 'Un-whitelist' : 'Whitelist'}
                        </button>
                      </>
                    ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', marginTop: '0.5rem' }}>
          {total.toLocaleString()} user{total === 1 ? '' : 's'}
        </div>
      )}
      <Pager page={page} totalPages={totalPages} onPage={setPage} disabled={loading} />
      </>)}

      {tab === 'infections' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>
              Live Trojan infections across the whole system, and attacker-side link status.
            </p>
            <button
              style={{ ...btnStyle('neutral'), marginLeft: 'auto' }}
              onClick={() => setInfRefresh(k => k + 1)}
              disabled={infLoading}
            >
              {infLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {infError && <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{infError}</p>}

          {infLoading && !infStats ? (
            <p style={{ color: '#666' }}>Loading infection stats…</p>
          ) : infStats ? (
            <>
              {/* Live infections (source of truth: infected nodes) */}
              <h3 style={{ color: '#cbd5e1', fontSize: '0.95rem', margin: '0 0 0.75rem' }}>Live infections (system)</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.75rem' }}>
                {statCard('Total infections', infStats.total, '#60a5fa', true)}
                {statCard('Active (earning)', infStats.active, '#4ade80')}
                {statCard('Installing (upload)', infStats.installing, '#fbbf24')}
                {statCard('On NPC nodes', infStats.onNpcNodes, '#a78bfa')}
                {statCard('On player nodes', infStats.onHumanNodes, '#f472b6')}
                {statCard('Infected nodes', infStats.infectedNodes, '#22d3ee')}
                {statCard('Distinct attackers', infStats.distinctAttackers, '#e2e8f0')}
              </div>

              {/* Attacker-side link tracking */}
              <h3 style={{ color: '#cbd5e1', fontSize: '0.95rem', margin: '0 0 0.75rem' }}>Attacker-side tracking</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
                {statCard('Tracked links', infStats.attackerTracked, '#60a5fa')}
                {statCard('Connection lost', infStats.connectionLost, '#f87171')}
              </div>
              <p style={{ color: '#666', fontSize: '0.75rem', marginTop: '1rem' }}>
                "Connection lost" = the infected node changed IP, so the attacker can't collect until they re-hack it
                (the infection itself keeps running and is still counted above).
              </p>
            </>
          ) : (
            <p style={{ color: '#666' }}>No data.</p>
          )}
        </div>
      )}

      {tab === 'software' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>
              Highest software version reached per type across all players (admins excluded).
            </p>
            <button
              style={{ ...btnStyle('neutral'), marginLeft: 'auto' }}
              onClick={() => setSwRefresh(k => k + 1)}
              disabled={swLoading}
            >
              {swLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {swError && <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{swError}</p>}

          {swLoading && !swStats ? (
            <p style={{ color: '#666' }}>Loading software stats…</p>
          ) : swStats && swStats.length > 0 ? (
            <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #222', color: '#666', textAlign: 'left' }}>
                    <th style={{ padding: '0.75rem 1rem' }}>Software</th>
                    <th style={{ padding: '0.75rem 1rem' }}>Max level</th>
                    <th style={{ padding: '0.75rem 1rem' }}>Player</th>
                  </tr>
                </thead>
                <tbody>
                  {swStats.map(row => (
                    <tr key={row.type} style={{ borderBottom: '1px solid #1a1a1a' }}>
                      <td style={{ padding: '0.75rem 1rem', color: '#e2e8f0' }}>{row.type}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--primary-color)', fontWeight: 700 }}>v{row.maxVersion.toFixed(1)}</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#9ca3af' }}>{row.username}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#666' }}>No software owned by any player yet.</p>
          )}
        </div>
      )}

      {/* Ban modal */}
      {banModal && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '420px', padding: '1.5rem' }}>
            <h3 style={{ marginBottom: '1.25rem', color: '#ef4444' }}>
              Ban: {banModal.username}
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                Reason
              </label>
              <input
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                placeholder="Reason for the ban…"
                value={banReason}
                onChange={e => setBanReason(e.target.value)}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                Duration
              </label>
              <select
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', cursor: 'pointer' }}
                value={banDuration === undefined ? '' : banDuration}
                onChange={e => setBanDuration(e.target.value === '' ? undefined : Number(e.target.value))}
              >
                {DURATION_OPTIONS.map(opt => (
                  <option key={opt.label} value={opt.value ?? ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {actionError && (
              <p style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '0.875rem' }}>{actionError}</p>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button style={{ ...btnStyle('danger'), padding: '0.5rem 1.25rem' }} onClick={handleBan}>
                Confirm Ban
              </button>
              <button style={{ ...btnStyle('neutral'), padding: '0.5rem 1.25rem' }} onClick={() => setBanModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity modal — last 50 actions for a player */}
      {activityModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
          }}
          onClick={() => setActivityModal(null)}
        >
          <div
            className="card"
            style={{ width: '100%', maxWidth: '560px', maxHeight: '78vh', display: 'flex', flexDirection: 'column', padding: '1.25rem' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: 'var(--primary-color)', margin: 0 }}>
                Activity: {activityModal.username}
              </h3>
              <button style={btnStyle('neutral')} onClick={() => setActivityModal(null)}>Close</button>
            </div>

            {activityLoading ? (
              <p style={{ color: '#666' }}>Loading…</p>
            ) : activityEntries.length === 0 ? (
              <p style={{ color: '#555' }}>No recorded activity.</p>
            ) : (
              <div style={{ overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #222', color: '#666', textAlign: 'left' }}>
                      <th style={{ padding: '0.4rem 0.5rem' }}>Time</th>
                      <th style={{ padding: '0.4rem 0.5rem' }}>Action</th>
                      <th style={{ padding: '0.4rem 0.5rem' }}>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityEntries.map(e => (
                      <tr key={e.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                        <td style={{ padding: '0.4rem 0.5rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>{fmtDateTime(e.timestamp)}</td>
                        <td style={{ padding: '0.4rem 0.5rem', color: '#e2e8f0' }}>{e.action}</td>
                        <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{e.ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Earnings modal — lifetime totals + last 50 gains for a player */}
      {earningsModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
          }}
          onClick={() => setEarningsModal(null)}
        >
          <div
            className="card"
            style={{ width: '100%', maxWidth: '620px', maxHeight: '78vh', display: 'flex', flexDirection: 'column', padding: '1.25rem' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: 'var(--primary-color)', margin: 0 }}>
                Earnings: {earningsModal.username}
              </h3>
              <button style={btnStyle('neutral')} onClick={() => setEarningsModal(null)}>Close</button>
            </div>

            {earningsLoading ? (
              <p style={{ color: '#666' }}>Loading…</p>
            ) : !earningsData || earningsData.entries.length === 0 ? (
              <p style={{ color: '#555' }}>No recorded earnings.</p>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  <span style={{ color: '#fbbf24' }}>Σ {earningsData.totals.credits.toLocaleString()} cr</span>
                  <span style={{ color: '#4ade80' }}>Σ {earningsData.totals.experience.toLocaleString()} XP</span>
                  <span style={{ color: '#60a5fa' }}>Σ {earningsData.totals.reputation.toLocaleString()} Rep</span>
                </div>
                <div style={{ overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #222', color: '#666', textAlign: 'left' }}>
                        <th style={{ padding: '0.4rem 0.5rem' }}>Time</th>
                        <th style={{ padding: '0.4rem 0.5rem' }}>Source</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Cr</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>XP</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Rep</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earningsData.entries.map(e => (
                        <tr key={e.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                          <td style={{ padding: '0.4rem 0.5rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>{fmtDateTime(e.timestamp)}</td>
                          <td style={{ padding: '0.4rem 0.5rem', color: '#e2e8f0' }}>{e.source}</td>
                          <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: e.credits ? '#fbbf24' : '#444' }}>{e.credits || '—'}</td>
                          <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: e.experience ? '#4ade80' : '#444' }}>{e.experience || '—'}</td>
                          <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: e.reputation ? '#60a5fa' : '#444' }}>{e.reputation || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
